/** Universal numbering: upper right 1-8, upper left 9-16, lower left 17-24, lower right 25-32 */
export const UPPER_RIGHT = [1, 2, 3, 4, 5, 6, 7, 8];
export const UPPER_LEFT = [9, 10, 11, 12, 13, 14, 15, 16];
export const LOWER_LEFT = [17, 18, 19, 20, 21, 22, 23, 24];
export const LOWER_RIGHT = [25, 26, 27, 28, 29, 30, 31, 32];
export function isUpper(toothNumber) {
  return toothNumber >= 1 && toothNumber <= 16;
}
export function isLower(toothNumber) {
  return toothNumber >= 17 && toothNumber <= 32;
}

/** Surface labels for display */
export const SURFACE_LABELS = {
  M: "Mesial",
  D: "Distal",
  O: "Occlusal",
  I: "Incisal",
  B: "Buccal",
  F: "Facial",
  L: "Lingual",
  P: "Palatal"
};

/** Tooth type by number for anatomical shapes */

export function getToothShape(toothNumber) {
  const n = toothNumber <= 16 ? toothNumber : 33 - toothNumber;
  if (n <= 3) return "molar"; // 1-3, 14-16, 17-19, 30-32
  if (n <= 5) return "premolar"; // 4-5, 12-13, 20-21, 28-29
  if (n === 6) return "canine"; // 6, 11, 22, 27
  return "incisor"; // 7-8, 9-10, 23-24, 25-26
}

/** Unified clinical color palette for surface conditions — single source of truth */
export const CLINICAL_COLORS = {
  planned: "#3b82f6",
  // blue
  caries: "#ef4444",
  // red
  composite: "#22c55e",
  // green
  amalgam: "#6b7280",
  // gray
  otherRestoration: "#22c55e",
  // green
  watch: "#eab308",
  // yellow
  healthy: "transparent"
};

/** Shared interaction colors for surface selection/hover — single source of truth */
export const INTERACTION_COLORS = {
  selectedFill: "#bfdbfe",
  // blue-200
  selectedStroke: "#2563eb",
  // blue-600
  hoverFill: "#eff6ff",
  // blue-50
  hoverStroke: "#60a5fa",
  // blue-400
  defaultStroke: "#b0a080" // warm outline
};

/** Stroke color for a surface fill — defaults to warm outline for transparent fills */
export function getDefaultSurfaceStroke(fill) {
  if (fill === "transparent") return INTERACTION_COLORS.defaultStroke;
  return fill;
}

/** Fill color for a surface status */
export function getSurfaceFill(s) {
  if (s.planned) return CLINICAL_COLORS.planned;
  if (s.condition === "caries") return CLINICAL_COLORS.caries;
  if (s.restoration === "composite" || s.restoration === "filling") return CLINICAL_COLORS.composite;
  if (s.restoration === "amalgam") return CLINICAL_COLORS.amalgam;
  if (s.restoration) return CLINICAL_COLORS.otherRestoration;
  if (s.condition === "watch") return CLINICAL_COLORS.watch;
  return CLINICAL_COLORS.healthy;
}

/** Look up fill color for a named surface from a surfaces array */
export function getSurfaceFillByName(surface, surfaces) {
  if (!surfaces) return "transparent";
  const s = surfaces.find(sf => sf.surface === surface);
  if (!s) return "transparent";
  return getSurfaceFill(s);
}

/** Stroke color for tooth outline */
export function getToothStroke(condition, selected) {
  if (selected) return "#2563eb";
  if (condition === "missing") return "#d1d5db";
  if (condition === "crown") return "#2563eb";
  if (condition === "implant") return "#06b6d4";
  if (condition === "impacted") return "#a855f7";
  if (condition === "pontic") return "#9ca3af";
  return "#9ca3af";
}
export function getToothFill(condition) {
  if (condition === "missing") return "transparent";
  if (condition === "crown") return "#dbeafe";
  if (condition === "implant") return "#ecfeff";
  if (condition === "impacted") return "#f3e8ff";
  if (condition === "root_canal") return "#fef9c3";
  if (condition === "pontic") return "#f3f4f6";
  return "#fef3c7";
}

// ── PMS-standard Odontogram Colors ──

export const ODONTOGRAM_COLORS = {
  completed: "#388E3C",
  // green
  planned: "#448AFF",
  // blue
  plannedAlt: "#D50000",
  // red (alternative)
  missingSilhouette: "#E9E9E9",
  uneruptedFill: "#D9EEFC",
  uneruptedStroke: "#BBDEFB",
  cariesOutline: "#000407",
  crownDetailStroke: "#A79B8E"
};

/** Default layer visibility state */
export const DEFAULT_LAYER_VISIBILITY = {
  completedProcedures: true,
  plannedProcedures: true,
  surfaceLabels: false,
  caries: true,
  bridges: true,
  existingRestorations: true,
  missingTeeth: true,
  implants: true,
  rootCanals: true,
  crowns: true,
  fractures: true
};

/** Map deciduous letters A-T to permanent tooth numbers */
export const DECIDUOUS_SLOT_MAP = {
  A: 4,
  B: 5,
  C: 6,
  D: 7,
  E: 8,
  F: 9,
  G: 10,
  H: 11,
  I: 12,
  J: 13,
  K: 20,
  L: 21,
  M: 22,
  N: 23,
  O: 24,
  P: 25,
  Q: 26,
  R: 27,
  S: 28,
  T: 29
};

/** Convert existing ToothCondition → ToothBaseStatus */
function conditionToBaseStatus(condition) {
  switch (condition) {
    case "missing":
      return "missing";
    case "impacted":
      return "impacted";
    case "supernumerary":
      return "supernumerary";
    default:
      return "permanent";
  }
}

/** Convert a ToothStatus (existing model) into EnhancedToothState */
export function toEnhancedToothState(tooth) {
  const baseStatus = conditionToBaseStatus(tooth.condition);

  // Build procedure overlays from condition
  const completedProcs = [];
  if (tooth.condition === "crown") completedProcs.push({
    type: "crown"
  });
  if (tooth.condition === "implant") completedProcs.push({
    type: "implant"
  });
  if (tooth.condition === "root_canal") completedProcs.push({
    type: "roots"
  });

  // Split surfaces into completed vs planned
  const completedSurfaces = [];
  const plannedSurfaces = [];
  const caries = [];
  for (const s of tooth.surfaces ?? []) {
    if (s.condition === "caries") {
      caries.push(s.surface);
    } else if (s.planned) {
      plannedSurfaces.push({
        surface: s.surface,
        color: ODONTOGRAM_COLORS.planned
      });
    } else if (s.restoration) {
      completedSurfaces.push({
        surface: s.surface,
        color: ODONTOGRAM_COLORS.completed
      });
    }
  }
  return {
    toothNumber: tooth.toothNumber,
    baseStatus,
    procedures: {
      completed: completedProcs,
      planned: []
    },
    surfaces: {
      completed: completedSurfaces,
      planned: plannedSurfaces
    },
    caries,
    notes: tooth.notes
  };
}

/** Estimate bounding box of an SVG path `d` string by parsing numeric coordinates.
 *  Approximation — parses all number pairs as x,y coordinates. */
export function estimatePathBounds(d) {
  const nums = d.match(/-?\d+\.?\d*/g)?.map(Number) ?? [];
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  for (let i = 0; i < nums.length - 1; i += 2) {
    const x = nums[i];
    const y = nums[i + 1];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (!isFinite(minX)) {
    minX = 0;
    maxX = 50;
    minY = 0;
    maxY = 100;
  }
  return {
    minX,
    maxX,
    minY,
    maxY,
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2
  };
}

/**
 * Quick-add procedure code categories — Archy-style hierarchical layout.
 * This is a curated subset for the quick-add UI panel.
 */

export const QUICK_CODE_CATEGORIES = [{
  name: "Exams",
  codes: [{
    label: "NP Exam",
    cdtCode: "D0150",
    description: "Comprehensive oral eval - new patient",
    highlight: true,
    feeCents: 12500
  }, {
    label: "Periodic",
    cdtCode: "D0120",
    description: "Periodic oral evaluation",
    highlight: true,
    feeCents: 6500
  }, {
    label: "Limited",
    cdtCode: "D0140",
    description: "Limited oral evaluation - problem focused",
    feeCents: 9500
  }, {
    label: "Comp Perio",
    cdtCode: "D0180",
    description: "Comprehensive periodontal evaluation",
    feeCents: 12500
  }]
}, {
  name: "X-rays",
  codes: [{
    label: "BWX (4)",
    cdtCode: "D0274",
    description: "Bitewings - four radiographic images",
    highlight: true,
    feeCents: 7500
  }, {
    label: "PA",
    cdtCode: "D0220",
    description: "Intraoral - periapical first radiographic image",
    feeCents: 3500
  }, {
    label: "Pano",
    cdtCode: "D0330",
    description: "Panoramic radiographic image",
    highlight: true,
    feeCents: 15000
  }, {
    label: "FMX",
    cdtCode: "D0210",
    description: "Intraoral - complete series",
    feeCents: 18000
  }, {
    label: "BWX (2)",
    cdtCode: "D0272",
    description: "Bitewings - two radiographic images",
    feeCents: 4500
  }, {
    label: "PA ea addl",
    cdtCode: "D0230",
    description: "Intraoral - periapical each additional",
    feeCents: 2500
  }]
}, {
  name: "Preventive",
  codes: [{
    label: "Prophy",
    cdtCode: "D1110",
    description: "Prophylaxis - adult",
    highlight: true,
    feeCents: 11500
  }, {
    label: "Child Prophy",
    cdtCode: "D1120",
    description: "Prophylaxis - child",
    feeCents: 7500
  }, {
    label: "Fl Varnish",
    cdtCode: "D1206",
    description: "Topical application of fluoride varnish",
    highlight: true,
    feeCents: 4000
  }, {
    label: "Sealant",
    cdtCode: "D1351",
    description: "Sealant - per tooth",
    feeCents: 5500
  }, {
    label: "Perio Maint",
    cdtCode: "D4910",
    description: "Periodontal maintenance",
    feeCents: 16000
  }]
}, {
  name: "Restorative",
  codes: [{
    label: "Comp 1 surf",
    cdtCode: "D2391",
    description: "Resin-based composite - one surface, posterior",
    highlight: true,
    feeCents: 22500
  }, {
    label: "Comp 2 surf",
    cdtCode: "D2392",
    description: "Resin-based composite - two surfaces, posterior",
    feeCents: 29500
  }, {
    label: "Comp 3 surf",
    cdtCode: "D2393",
    description: "Resin-based composite - three surfaces, posterior",
    feeCents: 35500
  }, {
    label: "Comp 4+ surf",
    cdtCode: "D2394",
    description: "Resin-based composite - four+ surfaces, posterior",
    feeCents: 42000
  }, {
    label: "Ant Comp 1",
    cdtCode: "D2330",
    description: "Resin-based composite - one surface, anterior",
    feeCents: 20000
  }, {
    label: "Ant Comp 2",
    cdtCode: "D2331",
    description: "Resin-based composite - two surfaces, anterior",
    feeCents: 25000
  }, {
    label: "Ant Comp 3",
    cdtCode: "D2332",
    description: "Resin-based composite - three surfaces, anterior",
    feeCents: 30000
  }, {
    label: "Amalgam 1",
    cdtCode: "D2140",
    description: "Amalgam - one surface, primary or permanent",
    feeCents: 18000
  }]
}, {
  name: "Crowns/Bridges",
  codes: [{
    label: "Porc Crown",
    cdtCode: "D2740",
    description: "Crown - porcelain/ceramic",
    highlight: true,
    feeCents: 135000
  }, {
    label: "PFM Crown",
    cdtCode: "D2750",
    description: "Crown - porcelain fused to high noble metal",
    highlight: true,
    feeCents: 125000
  }, {
    label: "Build Up",
    cdtCode: "D2950",
    description: "Core buildup, including any pins when required",
    feeCents: 35000
  }, {
    label: "FG Crown",
    cdtCode: "D2790",
    description: "Crown - full cast high noble metal",
    feeCents: 120000
  }, {
    label: "PFM Pontic",
    cdtCode: "D6752",
    description: "Retainer crown - PFM",
    feeCents: 125000
  }, {
    label: "Porc Ret",
    cdtCode: "D6740",
    description: "Retainer crown - porcelain/ceramic",
    feeCents: 135000
  }]
}, {
  name: "Endodontics",
  codes: [{
    label: "RCT Anterior",
    cdtCode: "D3310",
    description: "Endodontic therapy, anterior tooth",
    highlight: true,
    feeCents: 85000
  }, {
    label: "RCT Premolar",
    cdtCode: "D3320",
    description: "Endodontic therapy, premolar tooth",
    feeCents: 95000
  }, {
    label: "RCT Molar",
    cdtCode: "D3330",
    description: "Endodontic therapy, molar tooth",
    highlight: true,
    feeCents: 115000
  }, {
    label: "Pulp Cap",
    cdtCode: "D3110",
    description: "Pulp cap - direct",
    feeCents: 15000
  }, {
    label: "Pulpotomy",
    cdtCode: "D3220",
    description: "Therapeutic pulpotomy",
    feeCents: 25000
  }]
}, {
  name: "Oral Surgery",
  codes: [{
    label: "Ext Simple",
    cdtCode: "D7140",
    description: "Extraction, erupted tooth or exposed root",
    highlight: true,
    feeCents: 25000
  }, {
    label: "Ext Surgical",
    cdtCode: "D7210",
    description: "Extraction, erupted tooth - surgical",
    feeCents: 35000
  }, {
    label: "Soft Tissue",
    cdtCode: "D7220",
    description: "Removal of impacted tooth - soft tissue",
    feeCents: 40000
  }, {
    label: "Partial Bony",
    cdtCode: "D7230",
    description: "Removal of impacted tooth - partial bony",
    feeCents: 50000
  }, {
    label: "Full Bony",
    cdtCode: "D7240",
    description: "Removal of impacted tooth - complete bony",
    feeCents: 60000
  }]
}, {
  name: "Periodontics",
  codes: [{
    label: "SRP 4+ teeth",
    cdtCode: "D4341",
    description: "Periodontal scaling and root planing, 4+ teeth per quad",
    highlight: true,
    feeCents: 30000
  }, {
    label: "SRP 1-3 teeth",
    cdtCode: "D4342",
    description: "Periodontal scaling and root planing, 1-3 teeth per quad",
    feeCents: 22000
  }, {
    label: "Full Debride",
    cdtCode: "D4355",
    description: "Full mouth debridement",
    feeCents: 18000
  }, {
    label: "Gingivectomy",
    cdtCode: "D4210",
    description: "Gingivectomy/gingivoplasty - per quadrant",
    feeCents: 45000
  }, {
    label: "Osseous Surg",
    cdtCode: "D4260",
    description: "Osseous surgery - per quadrant",
    feeCents: 95000
  }]
}, {
  name: "Adjunctive",
  codes: [{
    label: "Palliative",
    cdtCode: "D9110",
    description: "Palliative treatment of dental pain",
    feeCents: 12000
  }, {
    label: "Nitrous",
    cdtCode: "D9230",
    description: "Inhalation of nitrous oxide/analgesia",
    feeCents: 5000
  }, {
    label: "Occlusal Guard",
    cdtCode: "D9944",
    description: "Occlusal guard - hard appliance",
    feeCents: 55000
  }, {
    label: "Desentize",
    cdtCode: "D9910",
    description: "Application of desensitizing agent",
    feeCents: 4000
  }]
}];

// ─── Clinical Finding Definitions ─────────────────────────────────────────────

export const CLINICAL_FINDING_DEFS = [{
  type: "caries",
  label: "Caries / Decay",
  shortLabel: "Caries",
  description: "Active decay requiring treatment",
  color: "#dc2626",
  bgColor: "#fef2f2",
  icon: "⬤",
  requiresSurfaces: true
}, {
  type: "watch",
  label: "Watch / Incipient",
  shortLabel: "Watch",
  description: "Early-stage decay — monitor, no treatment yet",
  color: "#d97706",
  bgColor: "#fffbeb",
  icon: "◐",
  requiresSurfaces: true
}, {
  type: "fracture",
  label: "Fracture / Crack",
  shortLabel: "Fracture",
  description: "Cracked or fractured tooth structure",
  color: "#7c3aed",
  bgColor: "#f5f3ff",
  icon: "⚡",
  requiresSurfaces: false
}, {
  type: "defective_restoration",
  label: "Defective Restoration",
  shortLabel: "Defect. Rest.",
  description: "Existing filling/restoration needs replacement",
  color: "#2563eb",
  bgColor: "#eff6ff",
  icon: "◇",
  requiresSurfaces: true
}, {
  type: "abscess",
  label: "Abscess",
  shortLabel: "Abscess",
  description: "Periapical or periodontal abscess",
  color: "#e11d48",
  bgColor: "#fff1f2",
  icon: "●",
  requiresSurfaces: false
}, {
  type: "recession",
  label: "Gingival Recession",
  shortLabel: "Recession",
  description: "Gum tissue loss exposing root surface",
  color: "#0891b2",
  bgColor: "#ecfeff",
  icon: "▼",
  requiresSurfaces: false
}, {
  type: "erosion",
  label: "Erosion",
  shortLabel: "Erosion",
  description: "Chemical wear of tooth enamel",
  color: "#ca8a04",
  bgColor: "#fefce8",
  icon: "△",
  requiresSurfaces: true
}, {
  type: "attrition",
  label: "Attrition / Wear",
  shortLabel: "Attrition",
  description: "Mechanical wear from grinding or bruxism",
  color: "#65a30d",
  bgColor: "#f7fee7",
  icon: "═",
  requiresSurfaces: false
}, {
  type: "hypersensitivity",
  label: "Hypersensitivity",
  shortLabel: "Sensitive",
  description: "Tooth sensitivity to temperature or pressure",
  color: "#0ea5e9",
  bgColor: "#f0f9ff",
  icon: "✧",
  requiresSurfaces: false
}, {
  type: "mobility",
  label: "Mobility",
  shortLabel: "Mobile",
  description: "Tooth mobility (Grade I–III)",
  color: "#f97316",
  bgColor: "#fff7ed",
  icon: "↔",
  requiresSurfaces: false
}, {
  type: "periapical_lesion",
  label: "Periapical Lesion",
  shortLabel: "PA Lesion",
  description: "Radiolucency at root apex indicating infection",
  color: "#b91c1c",
  bgColor: "#fef2f2",
  icon: "◉",
  requiresSurfaces: false
}, {
  type: "furcation",
  label: "Furcation Involvement",
  shortLabel: "Furcation",
  description: "Bone loss in the furcation area of multi-rooted teeth",
  color: "#9333ea",
  bgColor: "#faf5ff",
  icon: "⌂",
  requiresSurfaces: false
}, {
  type: "open_margin",
  label: "Open / Defective Margin",
  shortLabel: "Open Margin",
  description: "Gap between restoration and tooth structure",
  color: "#ea580c",
  bgColor: "#fff7ed",
  icon: "⊘",
  requiresSurfaces: true
}, {
  type: "open_contact",
  label: "Open Contact",
  shortLabel: "Open Contact",
  description: "Missing or inadequate contact between adjacent teeth",
  color: "#0d9488",
  bgColor: "#f0fdfa",
  icon: "⊗",
  requiresSurfaces: true
}];
export function getFindingDef(type) {
  return CLINICAL_FINDING_DEFS.find(d => d.type === type) ?? CLINICAL_FINDING_DEFS[0];
}
export function getFindingColor(finding) {
  return getFindingDef(finding.type).color;
}